// @ts-nocheck
/**
 * Giga — animated "context graph" banner (v6, always-alive).
 * Full graph on screen from the first frame with generous edge padding
 * (layout compressed 15% toward center). Perpetual organic motion: every
 * node wanders on a two-harmonic orbit (quick + erratic but readable) and
 * the wires flex to follow. Dashboard micro-events every 9s. Hover lifts
 * cards. Self-contained; loads DM Sans + Inter itself.
 * Usage: <GigaContextGraphBanner />  (16:9, fills container width, max 1440px)
 */
import { useEffect, useRef } from "react";
const CSS = `
  :root{
    --bg:#2B211A;
    --ink:#2B211A;
    --card:#FFFFFF;
    --card-tile:#F4EEE4;
    --card-divider:rgba(43,33,26,.08);
    --muted:rgba(43,33,26,.55);
    --label:rgba(43,33,26,.42);
    --accent:#EA580C;
    --line:rgba(244,240,234,.22);
    --ghost:#FFFFFF;
    --ghost-bar:#E4DFD6;
  }
  .banner, .banner *{box-sizing:border-box;margin:0;padding:0}

  .banner{
    position:relative;
    width:100%;
    max-width:1440px;
    aspect-ratio:16/9;
    margin:0 auto;
    background:radial-gradient(rgba(244,240,234,0.07) 0.8px, transparent 0.8px) 0 0/13px 13px, var(--bg);
    overflow:hidden;
  }

  /* ---------- connector lines ---------- */
  .banner #wires{position:absolute;inset:0;width:100%;height:100%;z-index:1;pointer-events:none}
  .banner #wires path.base{fill:none;stroke:var(--line);stroke-width:1;opacity:.75}

  /* ---------- generic node ---------- */
  .banner .node{position:absolute;z-index:2;transform:translate(-50%,-50%);}
  .banner .settled{opacity:1}
  .banner .enter{opacity:0}
  /* living-dashboard micro-events */
  .banner .evt{display:inline-block;animation:evtpop .7s cubic-bezier(.3,1.5,.5,1)}
  @keyframes evtpop{0%{transform:scale(1);filter:brightness(1)}35%{transform:scale(1.16);filter:brightness(1.55)}100%{transform:scale(1);filter:brightness(1)}}
  /* hover lift */
  .banner .card{transition:box-shadow .3s ease}
  .banner .card:hover{box-shadow:0 26px 54px -18px rgba(43,33,26,.3), 0 4px 10px rgba(43,33,26,.1)}
  @media (prefers-reduced-motion: reduce){
    .banner .evt{animation:none}
  }

  /* ---------- hero cards ---------- */
  .banner .card{
    background:var(--card);
    border:1px solid rgba(43,33,26,.08);
    border-radius:16px;
    padding:14px 16px 12px;
    width:248px;
    box-shadow:0 18px 40px -18px rgba(43,33,26,.28), 0 2px 6px rgba(43,33,26,.08);
    color:var(--ink);
    z-index:3;
  }
  .banner .card .head{display:flex;align-items:center;gap:11px}
  .banner .card .tile{
    width:38px;height:38px;flex:0 0 38px;border-radius:10px;
    background:var(--card-tile);
    display:flex;align-items:center;justify-content:center;
    position:relative;
  }
  .banner .card .titles{flex:1 1 auto;min-width:0}
  .banner .card .title{font:700 14px/1.25 "DM Sans",Inter,sans-serif;letter-spacing:-.01em;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
  .banner .card .title .dim{color:var(--muted);font-weight:500}
  .banner .card .sub{font:400 11px/1.3 Inter,sans-serif;color:var(--muted);margin-top:2px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
  .banner .card .divider{height:1px;background:var(--card-divider);margin:11px 0 9px}
  .banner .card .row{display:flex;align-items:center;gap:8px;padding:4px 0;font-size:11.5px}
  .banner .card .row svg{width:13px;height:13px;flex:0 0 13px;opacity:.55;stroke:var(--ink)}
  .banner .card .row .l{color:var(--label);width:74px;flex:0 0 74px}
  .banner .card .row .v{color:var(--ink);font-weight:600;white-space:nowrap}
  .banner .card .row .v.link{color:var(--accent);animation:glint 6s ease-in-out infinite}
  .banner .card .row .v.link .arr{display:inline-block;transform:translateY(-1px)}
  @keyframes glint{0%,86%,100%{filter:brightness(1)}92%{filter:brightness(1.45)}}
  .banner .badge{
    margin-left:auto;flex:0 0 auto;font:600 10px/1 Inter;padding:5px 8px;border-radius:999px;
    background:rgba(22,163,74,.1);color:#16A34A;display:flex;align-items:center;gap:5px;
  }
  .banner .badge i{width:5px;height:5px;border-radius:50%;background:#16A34A;display:block;animation:blink 3.5s ease-in-out infinite}
  @keyframes blink{0%,100%{opacity:1}50%{opacity:.35}}

  /* brand logos that were drawn white for the dark tiles */
  .banner #n-linear .tile svg.logo{fill:#5E6AD2}

  /* ---------- ghost cards ---------- */
  .banner .ghost{
    background:var(--ghost);
    border-radius:12px;
    padding:9px 12px;
    display:flex;align-items:center;gap:9px;
    box-shadow:0 8px 22px -12px rgba(43,33,26,.22), 0 1px 3px rgba(43,33,26,.06);
  }
  .banner .ghost .g-icon{width:22px;height:22px;flex:0 0 22px;display:flex;align-items:center;justify-content:center}
  .banner .ghost .bars{display:flex;flex-direction:column;gap:5px}
  .banner .ghost .bars i{display:block;height:6px;border-radius:3px;background:var(--ghost-bar)}
  .banner .ghost .bars i:nth-child(1){width:78px}
  .banner .ghost .bars i:nth-child(2){width:52px}
  .banner .ghost.sm .bars i:nth-child(1){width:46px}
  .banner .ghost.sm .bars i:nth-child(2){display:none}

  .banner .chip{
    background:var(--ghost);border-radius:11px;width:44px;height:44px;
    display:flex;align-items:center;justify-content:center;
    box-shadow:0 8px 22px -12px rgba(43,33,26,.25), 0 1px 3px rgba(43,33,26,.06);
  }
  .banner .chip.dark{background:var(--ink)}
  .banner .chip.pastel{background:#FBF9F5}

  /* Uniform logo scale — favicons are full-bleed squares (no internal
     padding like the old SVGs), so they render one size everywhere and get a
     hair of rounding to sit consistently in every container. */
  .banner svg.logo, .banner img.logo{width:19px;height:19px;display:block;object-fit:contain}
  .banner img.logo{border-radius:3px}
  .banner .ghost svg.logo, .banner .ghost img.logo{width:17px;height:17px}

  /* letter-mark tiles */
  .banner .lm{font:800 15px/1 "DM Sans";color:#fff;border-radius:6px;width:22px;height:22px;display:flex;align-items:center;justify-content:center}

  @media (max-width:760px){ .banner{display:none} } /* swap in the 9:16 asset on mobile */
`;
const MARKUP = `<svg id="wires"></svg>

  <!-- ================= HERO CARDS ================= -->

  <div class="node card float enter" id="n-linear" style="left:32%;top:23%;--fd:8s;--edelay:.1s">
    <div class="head">
      <div class="tile">
        <!-- Linear -->
        <svg class="logo" viewBox="0 0 100 100" fill="#fff"><path d="M1.6 61.3 38.7 98.4C19.5 94 5.9 80.5 1.6 61.3ZM.1 46.6 53.4 99.9c4.4-.4 8.7-1.4 12.7-3L3.1 33.9c-1.6 4-2.6 8.3-3 12.7ZM7.6 24.1 75.9 92.4c3.2-2.1 6.2-4.6 8.9-7.4L15 15.2c-2.8 2.7-5.3 5.7-7.4 8.9ZM50 0C22.4 0 0 22.4 0 50c0-1.7.1-3.4.3-5L55 99.7c26.1-2.5 45-24.6 45-49.7C100 22.4 77.6 0 50 0Z" transform="scale(.9) translate(5,5)"/></svg>
      </div>
      <div class="titles">
        <div class="title">Fix onboarding flow</div>
        <div class="sub">ENG-462 · In Progress</div>
      </div>
    </div>
    <div class="divider"></div>
    <div class="row"><svg viewBox="0 0 16 16" fill="none" stroke="#fff" stroke-width="1.4"><rect x="2" y="3" width="12" height="11" rx="2"/><path d="M2 6.5h12M5.5 1.5v3M10.5 1.5v3"/></svg><span class="l">Status</span><span class="v">In Progress</span></div>
    <div class="row"><svg viewBox="0 0 16 16" fill="none" stroke="#fff" stroke-width="1.4"><circle cx="8" cy="5" r="2.6"/><path d="M2.8 14c.7-3 2.8-4.5 5.2-4.5S12.5 11 13.2 14"/></svg><span class="l">Assignee</span><span class="v">Naveen S.</span></div>
    <div class="row"><svg viewBox="0 0 16 16" fill="none" stroke="#fff" stroke-width="1.4"><rect x="2" y="3" width="12" height="11" rx="2"/><path d="M2 6.5h12"/></svg><span class="l">Due</span><span class="v">Next Friday</span></div>
  </div>

  <div class="node card float enter" id="n-gcal" style="left:66%;top:24%;width:262px;--fd:9s;--fdelay:1.2s;--edelay:.25s">
    <div class="head">
      <div class="tile">
        <!-- Google Calendar -->
        <svg class="logo" viewBox="0 0 24 24"><rect x="3" y="3" width="18" height="18" rx="2.5" fill="#fff"/><rect x="3" y="3" width="18" height="4.5" fill="#1A73E8"/><rect x="3" y="16.5" width="4.5" height="4.5" fill="#188038"/><rect x="16.5" y="16.5" width="4.5" height="4.5" fill="#FBBC04"/><text x="12" y="15.6" font-family="DM Sans" font-weight="800" font-size="8" fill="#1A73E8" text-anchor="middle">31</text></svg>
      </div>
      <div class="titles">
        <div class="title">Investor Update Call</div>
        <div class="sub">Tomorrow · 10:00 AM</div>
      </div>
    </div>
    <div class="divider"></div>
    <div class="row"><svg viewBox="0 0 16 16" fill="none" stroke="#fff" stroke-width="1.4"><rect x="2" y="3" width="12" height="11" rx="2"/><path d="M2 6.5h12"/></svg><span class="l">Date</span><span class="v">Tomorrow, 10:00 AM</span></div>
    <div class="row"><svg viewBox="0 0 16 16" fill="none" stroke="#fff" stroke-width="1.4"><circle cx="8" cy="5" r="2.6"/><path d="M2.8 14c.7-3 2.8-4.5 5.2-4.5S12.5 11 13.2 14"/></svg><span class="l">Organizer</span><span class="v">Faith Ellis</span></div>
    <div class="row"><svg viewBox="0 0 16 16" fill="none" stroke="#fff" stroke-width="1.4"><path d="M8 14s4.8-4 4.8-7.4A4.8 4.8 0 0 0 3.2 6.6C3.2 10 8 14 8 14Z"/><circle cx="8" cy="6.6" r="1.6"/></svg><span class="l">Location</span><span class="v">Zoom</span></div>
  </div>

  <div class="node card float enter" id="n-shopify" style="left:17.5%;top:56%;width:252px;--fd:7.5s;--fdelay:.6s;--edelay:.4s">
    <div class="head">
      <div class="tile">
        <!-- Shopify -->
        <svg class="logo" viewBox="0 0 24 24" fill="none"><path d="M9.5 5.5C10 4 11 3 12.2 3c.5 0 .9.2 1.2.5.9-.3 1.6 0 2 .8l1.6 1 .1.6 1.5 12.6-6.4 1.9L5 18.9 6.7 6.3l2.8-.8Z" fill="#95BF47"/><path d="M13.4 3.5c.9-.3 1.6 0 2 .8l1.6 1 .1.6 1.5 12.6-6.4 1.9V3.9c.4-.3.8-.4 1.2-.4Z" fill="#5E8E3E"/><path d="M12.6 8.6c-.5-.2-1.4-.3-2 .2-.5.4-.7 1-.5 1.6.3.9 1.9 1 2.2 2.2.3 1-.4 2-1.5 2.2-.9.2-1.7-.2-2.1-.5l.3-1.1s.7.4 1.3.4c.4 0 .6-.3.5-.6-.2-.9-1.9-1-2.2-2.3-.3-1.2.6-2.6 2.5-2.8.7-.1 1.2.1 1.2.1l-.3 1.6Z" fill="#fff"/></svg>
      </div>
      <div class="titles">
        <div class="title">Order #2841</div>
        <div class="sub">12 minutes ago</div>
      </div>
      <div class="badge" id="b-paid"><i></i>Paid</div>
    </div>
    <div class="divider"></div>
    <div class="row"><svg viewBox="0 0 16 16" fill="none" stroke="#fff" stroke-width="1.4"><rect x="2.5" y="2.5" width="11" height="11" rx="2"/><path d="M5.5 6h5M5.5 9h3.5"/></svg><span class="l">Total</span><span class="v">$186.40</span></div>
    <div class="row"><svg viewBox="0 0 16 16" fill="none" stroke="#fff" stroke-width="1.4"><circle cx="8" cy="5" r="2.6"/><path d="M2.8 14c.7-3 2.8-4.5 5.2-4.5S12.5 11 13.2 14"/></svg><span class="l">Customer</span><span class="v">Maya R.</span></div>
    <div class="row"><svg viewBox="0 0 16 16" fill="none" stroke="#fff" stroke-width="1.4"><path d="M3 5.5 8 3l5 2.5v5L8 13l-5-2.5v-5Z"/></svg><span class="l">Items</span><span class="v">3</span></div>
  </div>

  <div class="node card float enter" id="n-stripe" style="left:49.5%;top:52%;width:300px;z-index:4;--fd:8.5s;--fdelay:2s;--edelay:.55s">
    <div class="head">
      <div class="tile">
        <!-- Stripe -->
        <svg class="logo" viewBox="0 0 24 24"><rect width="24" height="24" rx="5" fill="#635BFF"/><path d="M11.2 9.6c0-.6.5-.9 1.3-.9 1.2 0 2.8.4 4 1V6.2A10 10 0 0 0 12.5 5.5c-3.2 0-5.4 1.7-5.4 4.5 0 4.4 6 3.7 6 5.6 0 .8-.7 1-1.6 1-1.3 0-3-.6-4.4-1.3v3.6c1.5.6 3 .9 4.4.9 3.3 0 5.6-1.6 5.6-4.5 0-4.7-6-3.9-6-5.7Z" fill="#fff" transform="scale(.8) translate(3,3)"/></svg>
      </div>
      <div class="titles">
        <div class="title">Growth Plan</div>
        <div class="sub">$299/mo · Renews Apr 30</div>
      </div>
      <div class="badge"><i></i>Active</div>
    </div>
    <div class="divider"></div>
    <div class="row"><svg viewBox="0 0 16 16" fill="none" stroke="#fff" stroke-width="1.4"><rect x="2.5" y="2.5" width="11" height="11" rx="2"/><path d="M5.5 6h5M5.5 9h3.5"/></svg><span class="l">Amount</span><span class="v">$299/mo</span></div>
    <div class="row"><svg viewBox="0 0 16 16" fill="none" stroke="#fff" stroke-width="1.4"><circle cx="8" cy="8" r="5.5"/><path d="M8 5v3l2 1.5"/></svg><span class="l">Customer</span><span class="v link">Acme Corp <span class="arr">↗</span></span></div>
    <div class="row"><svg viewBox="0 0 16 16" fill="none" stroke="#fff" stroke-width="1.4"><rect x="3" y="6.5" width="10" height="7" rx="1.5"/><path d="M5.5 6.5v-2a2.5 2.5 0 0 1 5 0v2"/></svg><span class="l">Renews</span><span class="v">Apr 30, 2026</span></div>
  </div>

  <div class="node card float enter" id="n-granola" style="left:81%;top:55%;width:252px;--fd:7s;--fdelay:1.6s;--edelay:.7s">
    <div class="head">
      <div class="tile">
        <!-- Granola (real logo, pulled from granola.ai) -->
        <img class="logo" src="https://www.google.com/s2/favicons?domain=granola.ai&amp;sz=64" alt="Granola" loading="lazy"/>
      </div>
      <div class="titles">
        <div class="title">Investor Update — Notes</div>
        <div class="sub">Granola · synced 2 min ago</div>
      </div>
    </div>
    <div class="divider"></div>
    <div class="row"><svg viewBox="0 0 16 16" fill="none" stroke="#fff" stroke-width="1.4"><path d="M4 2.5h8a1.5 1.5 0 0 1 1.5 1.5v9.5l-3-2h-6.5A1.5 1.5 0 0 1 2.5 10V4A1.5 1.5 0 0 1 4 2.5Z"/></svg><span class="l">Summary</span><span class="v" id="v-granola">3 action items</span></div>
    <div class="row"><svg viewBox="0 0 16 16" fill="none" stroke="#fff" stroke-width="1.4"><circle cx="8" cy="5" r="2.6"/><path d="M2.8 14c.7-3 2.8-4.5 5.2-4.5S12.5 11 13.2 14"/></svg><span class="l">With</span><span class="v">Alex Rivera</span></div>
    <div class="row"><svg viewBox="0 0 16 16" fill="none" stroke="#fff" stroke-width="1.4"><path d="M6.5 9.5 13 3M9 3h4v4"/><path d="M13 9.5V12a1.5 1.5 0 0 1-1.5 1.5h-7A1.5 1.5 0 0 1 3 12V5a1.5 1.5 0 0 1 1.5-1.5H6"/></svg><span class="l">Assigned</span><span class="v link">in Linear <span class="arr">↗</span></span></div>
  </div>

  <div class="node card float enter" id="n-slack" style="left:33%;top:82%;width:252px;--fd:9s;--fdelay:.3s;--edelay:.85s">
    <div class="head">
      <div class="tile">
        <!-- Slack -->
        <svg class="logo" viewBox="0 0 24 24"><g><path fill="#E01E5A" d="M5.1 14.9a2 2 0 1 1-2-2h2v2Zm1 0a2 2 0 0 1 4 0v5a2 2 0 1 1-4 0v-5Z"/><path fill="#36C5F0" d="M9.1 5.1a2 2 0 1 1 2-2v2h-2Zm0 1a2 2 0 0 1 0 4h-5a2 2 0 1 1 0-4h5Z"/><path fill="#2EB67D" d="M18.9 9.1a2 2 0 1 1 2 2h-2v-2Zm-1 0a2 2 0 0 1-4 0v-5a2 2 0 1 1 4 0v5Z"/><path fill="#ECB22E" d="M14.9 18.9a2 2 0 1 1-2 2v-2h2Zm0-1a2 2 0 0 1 0-4h5a2 2 0 1 1 0 4h-5Z"/></g></svg>
      </div>
      <div class="titles">
        <div class="title">#growth <span class="dim" id="v-slack">· 3 new messages</span></div>
        <div class="sub">sarah · 15 minutes ago</div>
      </div>
    </div>
    <div class="divider"></div>
    <div class="row"><svg viewBox="0 0 16 16" fill="none" stroke="#fff" stroke-width="1.4"><path d="M5 2.5 4 13.5M12 2.5l-1 11M2.8 5.5h11M2.2 10.5h11"/></svg><span class="l">Channel</span><span class="v">#growth</span></div>
    <div class="row"><svg viewBox="0 0 16 16" fill="none" stroke="#fff" stroke-width="1.4"><circle cx="8" cy="5" r="2.6"/><path d="M2.8 14c.7-3 2.8-4.5 5.2-4.5S12.5 11 13.2 14"/></svg><span class="l">From</span><span class="v">Sarah Chen</span></div>
    <div class="row"><svg viewBox="0 0 16 16" fill="none" stroke="#fff" stroke-width="1.4"><rect x="2" y="3" width="12" height="11" rx="2"/><path d="M2 6.5h12"/></svg><span class="l">Date</span><span class="v">Today, 4:32 PM</span></div>
  </div>

  <div class="node card float enter" id="n-dropbox" style="left:67.5%;top:85%;width:246px;--fd:8s;--fdelay:2.4s;--edelay:1s">
    <div class="head">
      <div class="tile">
        <!-- Dropbox -->
        <svg class="logo" viewBox="0 0 24 24" fill="#0061FF"><path d="m6.6 3 5.4 3.4L6.6 9.8 1.2 6.4 6.6 3Zm10.8 0 5.4 3.4-5.4 3.4L12 6.4 17.4 3ZM1.2 13.2 6.6 9.8l5.4 3.4-5.4 3.4-5.4-3.4Zm16.2-3.4 5.4 3.4-5.4 3.4-5.4-3.4 5.4-3.4ZM6.6 17.7l5.4-3.4 5.4 3.4L12 21l-5.4-3.3Z"/></svg>
      </div>
      <div class="titles">
        <div class="title">Q3_Board_Deck.pdf</div>
        <div class="sub">Updated yesterday</div>
      </div>
    </div>
    <div class="divider"></div>
    <div class="row"><svg viewBox="0 0 16 16" fill="none" stroke="#fff" stroke-width="1.4"><path d="M4 1.5h5.5L13 5v9.5H4V1.5Z"/><path d="M9.5 1.5V5H13"/></svg><span class="l">Type</span><span class="v">PDF Document</span></div>
    <div class="row"><svg viewBox="0 0 16 16" fill="none" stroke="#fff" stroke-width="1.4"><circle cx="8" cy="5" r="2.6"/><path d="M2.8 14c.7-3 2.8-4.5 5.2-4.5S12.5 11 13.2 14"/></svg><span class="l">Owner</span><span class="v">Growth Team</span></div>
    <div class="row"><svg viewBox="0 0 16 16" fill="none" stroke="#fff" stroke-width="1.4"><circle cx="8" cy="8" r="5.5"/><path d="M8 5v3l2 1.5"/></svg><span class="l">Modified</span><span class="v">Yesterday</span></div>
  </div>`;
export function GigaContextGraphBanner() {
  const rootRef = useRef(null);
  useEffect(() => {
    const banner = rootRef.current;
    if (!banner) return;
    let stopped = false;
    let rafId = 0;
    let rto;
    if (!document.querySelector('link[data-giga-fonts]')) {
      const l = document.createElement("link");
      l.rel = "stylesheet";
      l.dataset.gigaFonts = "1";
      l.href = "https://fonts.googleapis.com/css2?family=DM+Sans:wght@500;700;800&family=Inter:wght@400;500;600;700&display=swap";
      document.head.appendChild(l);
    }

    /* ---------- logo library — every logo is the brand's REAL favicon (the
       exact multicolor icon people see in their browser tabs), served via
       Google's favicon service. No hand-drawn approximations. ---------- */
    const fav = (domain: string) => `<img class="logo" src="https://www.google.com/s2/favicons?domain=${domain}&amp;sz=64" alt="" loading="lazy"/>`;
    const LOGOS = {
      gmail: fav('mail.google.com'),
      gdrive: fav('drive.google.com'),
      notion: fav('notion.so'),
      linkedin: fav('linkedin.com'),
      hubspot: fav('hubspot.com'),
      figma: fav('figma.com'),
      github: fav('github.com'),
      zoom: fav('zoom.us'),
      salesforce: fav('salesforce.com'),
      granola: fav('granola.ai'),
      linear: fav('linear.app'),
      gcal: fav('calendar.google.com'),
      asana: fav('asana.com'),
      jira: fav('jira.atlassian.com'),
      intercom: fav('intercom.com'),
      airtable: fav('airtable.com'),
      googledocs: fav('docs.google.com'),
      googlesheets: fav('sheets.google.com'),
      loom: fav('loom.com'),
      outlook: fav('outlook.com'),
      quickbooks: fav('quickbooks.intuit.com'),
      trello: fav('trello.com'),
      monday: fav('monday.com'),
      confluence: fav('confluence.atlassian.com'),
      calendly: fav('calendly.com'),
      miro: fav('miro.com'),
      instagram: fav('instagram.com'),
      youtube: fav('youtube.com'),
      xtwitter: fav('x.com')
    };

    /* ---------- ghost nodes: [id, kind, logo, left%, top%, floatDur, delay]
       Every logo is a real product, used once. Positions are pushed toward the
       outer edges (top / bottom / far left / far right) so the ghosts peek out
       from behind the hero cards instead of being buried under them. ---------- */
    const GHOSTS = [
    // top edge
    ['g1', 'ghost', 'gmail', 14, 6, 8, .2], ['g2', 'ghost sm', 'asana', 44, 4, 9, 1.1], ['g3', 'ghost sm', 'trello', 57, 5.5, 7, .6], ['g6', 'ghost', 'instagram', 74, 6.5, 9, .4], ['g5', 'chip pastel', 'loom', 30, 6, 7.5, 1.4],
    // right edge
    ['g7', 'ghost sm', 'outlook', 91, 15, 8, 1.8], ['g11', 'ghost', 'confluence', 90, 31, 7.5, .5], ['g29', 'ghost sm', 'airtable', 92, 46, 8, .8], ['g25', 'ghost sm', 'calendly', 91, 62, 7.5, 2.3], ['g22', 'ghost', 'miro', 88, 80, 9, .9],
    // bottom edge
    ['g24', 'ghost sm', 'jira', 78, 93, 8, .6], ['g26', 'ghost', 'youtube', 52, 95, 8.5, 1.1], ['g27', 'ghost sm', 'monday', 25, 94, 7, .4], ['g19', 'chip', 'github', 44, 90, 8.5, 2.1], ['g20', 'chip', 'figma', 62, 88, 7.5, .2],
    // left edge
    ['g4', 'ghost', 'notion', 7, 18, 8.5, .9], ['g8', 'ghost', 'linkedin', 6, 33, 7, 1.2], ['g12', 'ghost', 'hubspot', 6, 48, 8, 1.5], ['g15', 'ghost sm', 'intercom', 5, 62, 7.5, 1.9], ['g23', 'ghost sm', 'zoom', 8, 78, 7, 1.4], ['g30', 'chip pastel', 'quickbooks', 12, 90, 7.5, 1.2],
    // inner peekers (still nudged off the hero centers)
    ['g9', 'chip', 'slackmini', 40, 20, 8, .8], ['g10', 'ghost', 'xtwitter', 26, 40, 9, 2], ['g14', 'chip pastel', 'gcal', 40, 60, 8.5, .3], ['g16', 'chip', 'stripemini', 74, 46, 8, 1], ['g17', 'ghost', 'salesforce', 74, 66, 9, .7], ['g18', 'chip pastel', 'linear', 60, 70, 7, 1.3]];
    LOGOS.slackmini = banner.querySelector('#n-slack .tile svg').outerHTML;
    LOGOS.stripemini = `<div class="lm" style="background:#635BFF;font-size:13px">S</div>`;
    if (!banner.querySelector('[data-ghost]')) for (const [id, kind, logo, left, top, fd, fdelay] of GHOSTS) {
      const el = document.createElement('div');
      const isChip = kind.startsWith('chip');
      el.className = `node float enter ${isChip ? kind : 'ghost ' + kind.replace('ghost', '').trim()}`;
      if (!isChip) el.classList.add('ghost');
      el.id = id;
      el.dataset.ghost = '1';
      el.style.cssText = `left:${left}%;top:${top}%;--fd:${fd}s;--fdelay:${fdelay}s`;
      el.innerHTML = isChip ? `<span class="g-icon">${LOGOS[logo] || ''}</span>` : `<span class="g-icon">${LOGOS[logo] || ''}</span><span class="bars"><i></i><i></i></span>`;
      banner.appendChild(el);
    }

    /* pull the whole layout toward the center: padding on all four edges */
    (function compressLayout() {
      const K = 0.85;
      banner.querySelectorAll('.node').forEach(el => {
        const x = parseFloat(el.style.left),
          y = parseFloat(el.style.top);
        if (isNaN(x) || isNaN(y)) return;
        el.style.left = (50 + (x - 50) * K).toFixed(2) + '%';
        el.style.top = (50 + (y - 50) * K).toFixed(2) + '%';
      });
    })();

    /* ---------- wires ---------- */
    const LINKS = [['n-stripe', 'n-linear'], ['n-stripe', 'n-gcal'], ['n-stripe', 'n-shopify'], ['n-stripe', 'n-granola'], ['n-stripe', 'n-slack'], ['n-stripe', 'n-dropbox'], ['n-granola', 'n-gcal'], ['n-granola', 'n-linear', true], ['n-slack', 'n-shopify'], ['n-dropbox', 'n-granola'], ['n-linear', 'g4'], ['n-linear', 'g2'], ['n-gcal', 'g6'], ['n-gcal', 'g5'], ['n-shopify', 'g12'], ['n-shopify', 'g8'], ['n-slack', 'g18'], ['n-slack', 'g27'], ['n-dropbox', 'g20'], ['n-dropbox', 'g26'], ['n-granola', 'g11'], ['n-granola', 'g25'], ['g1', 'n-linear'], ['g10', 'n-stripe'], ['g16', 'n-granola'], ['g19', 'n-slack'], ['g17', 'n-dropbox'], ['g9', 'n-gcal'], ['g14', 'n-stripe'], ['g23', 'n-slack'], ['g7', 'n-gcal'], ['g22', 'n-granola'], ['g24', 'n-dropbox'], ['g30', 'g15'], ['g29', 'n-granola'], ['g3', 'n-linear']];
    const svg = banner.querySelector('#wires');
    const NS = 'http://www.w3.org/2000/svg';
    const timers = [];
    const after = (sec, fn) => timers.push(setTimeout(fn, sec * 1000));

    /* ---------- IMMEDIATE REVEAL: everything is already on screen ---------- */
    function setEntranceChoreography() {
      banner.querySelectorAll('.node').forEach(el => {
        el.classList.remove('enter');
        el.classList.add('settled');
      });
    }
    function center(id) {
      const el = banner.querySelector('#' + id);
      const b = banner.getBoundingClientRect(),
        r = el.getBoundingClientRect();
      return [r.left - b.left + r.width / 2, r.top - b.top + r.height / 2];
    }
    let wireRefs = [];
    function drawWires() {
      svg.innerHTML = '';
      wireRefs = [];
      const b = banner.getBoundingClientRect();
      svg.setAttribute('viewBox', `0 0 ${b.width} ${b.height}`);
      LINKS.forEach(([a, c], i) => {
        const from = a,
          to = c;
        const [x1, y1] = center(from),
          [x2, y2] = center(to);
        const mx = (x1 + x2) / 2 + (i % 2 ? 28 : -28),
          my = (y1 + y2) / 2 + (i % 3 ? -22 : 22);
        const d = `M${x1},${y1} Q${mx},${my} ${x2},${y2}`;
        const base = document.createElementNS(NS, 'path');
        base.setAttribute('d', d);
        base.setAttribute('class', 'base');
        svg.appendChild(base);
        wireRefs.push({
          base,
          from,
          to,
          i
        });
      });
    }

    /* ---------- LIVING DRIFT: the settled graph keeps breathing ----------
       Each node floats on its own slow orbit (unique speed + phase); the wires
       are re-plotted every frame from the drifted positions, so the whole mesh
       flexes together like one living organism. */
    const REDUCED = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
    const DRIFT = {};
    let BASE = {};
    let idleOn = false;
    function measureBases() {
      BASE = {};
      const b = banner.getBoundingClientRect();
      banner.querySelectorAll('.node').forEach(el => {
        el.style.transform = 'translate(-50%,-50%)';
        const r = el.getBoundingClientRect();
        BASE[el.id] = [r.left - b.left + r.width / 2, r.top - b.top + r.height / 2];
      });
    }
    function initIdle() {
      banner.querySelectorAll('.node').forEach(el => {
        if (REDUCED) return;
        const hero = el.classList.contains('card');
        DRIFT[el.id] = {
          ax: (hero ? 3.5 : 6) + Math.random() * (hero ? 2 : 3),
          ay: (hero ? 3 : 5) + Math.random() * (hero ? 2 : 3),
          wx: 2 * Math.PI / (4 + Math.random() * 3),
          wy: 2 * Math.PI / (3.2 + Math.random() * 3),
          px: Math.random() * Math.PI * 2,
          py: Math.random() * Math.PI * 2,
          // second harmonic: quicker, smaller, out of phase — makes the path wander
          ax2: hero ? 1.2 : 2.2,
          ay2: hero ? 1 : 1.8,
          wx2: 2 * Math.PI / (1.6 + Math.random() * 1.4),
          wy2: 2 * Math.PI / (1.4 + Math.random() * 1.4),
          px2: Math.random() * Math.PI * 2,
          py2: Math.random() * Math.PI * 2
        };
      });
      measureBases();
      idleOn = true;
    }
    function idleFrame(s) {
      const CUR = {};
      for (const id in DRIFT) {
        const d = DRIFT[id],
          b = BASE[id];
        if (!b) continue;
        const dx = d.ax * Math.sin(d.wx * s + d.px) + d.ax2 * Math.sin(d.wx2 * s + d.px2);
        const dy = d.ay * Math.sin(d.wy * s + d.py) + d.ay2 * Math.sin(d.wy2 * s + d.py2);
        CUR[id] = [b[0] + dx, b[1] + dy];
        const el = banner.querySelector('#' + id);
        if (el) el.style.transform = `translate(calc(-50% + ${dx.toFixed(2)}px), calc(-50% + ${dy.toFixed(2)}px))`;
      }
      for (const w of wireRefs) {
        const A = CUR[w.from] || BASE[w.from],
          B = CUR[w.to] || BASE[w.to];
        if (!A || !B) continue;
        const mx = (A[0] + B[0]) / 2 + (w.i % 2 ? 28 : -28),
          my = (A[1] + B[1]) / 2 + (w.i % 3 ? -22 : 22);
        const d = `M${A[0].toFixed(1)},${A[1].toFixed(1)} Q${mx.toFixed(1)},${my.toFixed(1)} ${B[0].toFixed(1)},${B[1].toFixed(1)}`;
        w.base.setAttribute('d', d);
      }
    }
    let t0 = null;
    function tick(ts) {
      if (stopped) return;
      if (t0 === null) t0 = ts;
      const s = (ts - t0) / 1000;
      if (!idleOn) initIdle();
      if (!REDUCED) idleFrame(s);
      rafId = requestAnimationFrame(tick);
    }

    /* ---------- LIVING DASHBOARD (storyboard frames 7-8) ---------- */
    function flashEl(el) {
      if (!el) return;
      el.classList.remove('evt');
      void el.offsetWidth;
      el.classList.add('evt');
    }
    function tickValue(el, texts) {
      if (!el) return;
      flashEl(el);
      el.__i = 1 - (el.__i || 0);
      after(0.25, () => {
        el.textContent = texts[el.__i];
      });
    }
    function microCycle() {
      after(0.5, () => flashEl(banner.querySelector('#b-paid')));
      after(2.8, () => tickValue(banner.querySelector('#v-granola'), ['3 action items', '4 action items']));
      after(5.4, () => tickValue(banner.querySelector('#v-slack'), ['\u00b7 3 new messages', '\u00b7 4 new messages']));
    }
    function microLoop() {
      microCycle();
      timers.push(setTimeout(microLoop, 9000));
    }
    let started = false;
    const start = () => {
      if (stopped || started) return;
      started = true;
      setEntranceChoreography();
      drawWires();
      rafId = requestAnimationFrame(tick);
      after(1.2, microLoop);
    };
    const onResize = () => {
      clearTimeout(rto);
      rto = setTimeout(() => {
        drawWires();
        if (idleOn) measureBases();
      }, 120);
    };
    if (document.fonts && document.fonts.status !== "loaded") {
      document.fonts.ready.then(start);
      setTimeout(start, 1500);
    } else {
      start();
    }
    window.addEventListener("resize", onResize);
    return () => {
      stopped = true;
      cancelAnimationFrame(rafId);
      clearTimeout(rto);
      timers.forEach(clearTimeout);
      window.removeEventListener("resize", onResize);
    };
  }, []);
  return <>
      <style dangerouslySetInnerHTML={{
      __html: CSS
    }} data-id="element-3128" data-mp="2ynjom3avp0ey" />
      <div ref={rootRef} className="banner" dangerouslySetInnerHTML={{
      __html: MARKUP
    }} data-id="element-3129" data-mp="2tvi3drvqfzcs" />
    </>;
}